import clsx from "clsx";
import type { ReactNode } from "react";

interface ProgressRingProps {
  /** Validated (teal) and waiting for the SME (orange), out of `total`. */
  validated: number;
  waiting: number;
  total: number;
  size?: number;
  /** A word or number in the middle of the ring. */
  children?: ReactNode;
  className?: string;
}

const RADIUS = 15.9155; // a circle of circumference 100, so lengths read as percentages
const STROKE = 3.4;

function share(value: number, total: number): number {
  if (!Number.isFinite(value) || total <= 0) return 0;
  return Math.min(100, Math.max(0, (value / total) * 100));
}

/** The ring of a family or an item: how much is validated and how much waits for the SME. */
export function ProgressRing({ validated, waiting, total, size = 40, children, className }: ProgressRingProps) {
  const v = share(validated, total);
  const w = Math.min(share(waiting, total), 100 - v);
  const name =
    total <= 0
      ? "Nothing to count yet"
      : `${validated} of ${total} validated, ${waiting} waiting for the SME`;
  return (
    <span className={clsx("progress-ring", className)} style={{ width: size, height: size }} role="img" aria-label={name}>
      <svg viewBox="0 0 36 36" width={size} height={size} aria-hidden="true" focusable="false">
        <circle className="progress-ring__track" cx="18" cy="18" r={RADIUS} fill="none" strokeWidth={STROKE} />
        {w > 0 && (
          <circle
            className="progress-ring__waiting"
            cx="18"
            cy="18"
            r={RADIUS}
            fill="none"
            strokeWidth={STROKE}
            strokeDasharray={`${w} ${100 - w}`}
            strokeDashoffset={-v}
            transform="rotate(-90 18 18)"
          />
        )}
        {v > 0 && (
          <circle
            className="progress-ring__validated"
            cx="18"
            cy="18"
            r={RADIUS}
            fill="none"
            strokeWidth={STROKE}
            strokeDasharray={`${v} ${100 - v}`}
            transform="rotate(-90 18 18)"
          />
        )}
      </svg>
      {children !== undefined && <span className="progress-ring__label">{children}</span>}
    </span>
  );
}
